<template>
  <PortalLayout section="Send Multi-Building Alert">
    <div
      v-if="!property.isMultiAlertEnabled"
      class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4"
    >
      <div class="flex">
        <div class="flex-shrink-0">
          <i-heroicons-solid-exclamation class="h-5 w-5 text-yellow-400" />
        </div>
        <div class="ml-3">
          <p class="text-sm text-yellow-700">
            Multi-building alerts are not enabled for this organization.
          </p>
          <div class="mt-2">
            <router-link
              to="alert"
              class="text-sm font-medium text-yellow-700 underline hover:text-yellow-600"
            >
              Return to Single Building Alerts
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <!-- Navigation link back to single building -->
      <div class="mb-4">
        <router-link
          to="alert"
          class="inline-flex items-center text-blue-600 hover:text-blue-800 underline"
        >
          <i-heroicons-solid-arrow-left class="h-4 w-4 mr-1" />
          Send to One Building (allows better filtering)
        </router-link>
      </div>

      <!-- Building Selection -->
      <div class="mb-6">
        <h3 class="text-lg font-medium text-gray-900 mb-3">Select Buildings</h3>
        <Listbox v-model="selectedPortals" multiple class="lg:w-2/3">
          <div class="relative">
            <ListboxButton
              class="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg border shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
            >
              <span class="text-gray-800 text-md">
                <span v-if="selectedPortals.length === 0">-- Select Buildings --</span>
                <span v-else-if="selectedPortals.length === 1">{{
                  selectedPortals[0].name
                }}</span>
                <span v-else>{{ selectedPortals.length }} Buildings Selected</span>
              </span>
              <span
                class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
              >
                <i-heroicons-solid-selector
                  class="text-gray-600 w-5 h-5 text-2xl"
                  aria-hidden="true"
                />
              </span>
            </ListboxButton>
            <transition
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <ListboxOptions
                class="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-20"
              >
                <ListboxOption
                  v-for="portal in portals"
                  :key="portal.portalId"
                  :value="portal"
                >
                  <li
                    :class="[
                      selectedPortals.some((p) => p.portalId === portal.portalId)
                        ? 'border-1 bg-sky-300 border-blue-500'
                        : 'hover:bg-blue-200',
                      'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer flex items-center',
                    ]"
                  >
                    <!-- <input
                      type="checkbox"
                      :checked="
                        selectedPortals.some((p) => p.portalId === portal.portalId)
                      "
                      class="mr-2"
                      @click.stop
                    /> -->
                    {{ portal.name }}
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </transition>
          </div>
        </Listbox>

        <!-- Building Selection Quick Actions -->
        <div class="flex gap-2 mt-2">
          <button
            @click="selectAllBuildings"
            class="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          >
            Select All Buildings
          </button>
          <button
            @click="clearBuildingSelection"
            class="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            Clear Selection
          </button>
        </div>
      </div>

      <!-- Alert Template Selection -->
      <div class="mb-6">
        <h3 class="text-lg font-medium text-gray-900 mb-3">Alert Template</h3>
        <Listbox v-model="selectedTemplate" class="lg:w-1/2 3xl:w-1/3">
          <div class="relative mt-3">
            <ListboxButton
              class="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded-lg border shadow-md cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
            >
              <span class="text-gray-800' text-md">
                {{
                  alertTemplates.length > 0
                    ? selectedTemplate
                      ? selectedTemplate.name
                      : "-- select a template --"
                    : "-- no alert templates --"
                }}
              </span>
              <span
                class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
              >
                <i-heroicons-solid-selector
                  class="text-gray-600 w-5 h-5 text-2xl"
                  aria-hidden="true"
                />
              </span>
            </ListboxButton>
            <transition
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <ListboxOptions
                class="absolute w-full py-1 mt-1 z-20 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              >
                <ListboxOption
                  v-for="template in alertTemplates"
                  :key="template.templateId"
                  :value="template"
                >
                  <li
                    :class="[
                      selectedTemplate
                        ? selectedTemplate.templateId
                        : -1 == template.templateId
                        ? 'border-1 bg-sky-300 border-blue-500'
                        : 'hover:bg-blue-200',
                      'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                    ]"
                  >
                    {{ template.name }}
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </transition>
          </div>
        </Listbox>
      </div>

      <!-- Message Input -->
      <div class="mb-6">
        <h3 class="text-lg font-medium text-gray-900 mb-3">Alert Message</h3>
        <AlertMessageTextarea
          v-model="message"
          placeholder="Enter a message"
          @blur="mobileSummaryPanel?.generate()"
        />
        <AlertMobileSummary
          ref="mobileSummaryPanel"
          v-model:summary="mobileSummary"
          v-model:use-summary="useMobileSummary"
          :message="message"
          :portal-id="selectedPortals[0]?.portalId ?? property?.portalId"
          :sender-name="selectedPortals.length === 1 ? selectedPortals[0].name : property?.name"
        />
      </div>

      <!-- Simplified Recipient Selection -->
      <div class="mb-6">
        <h3 class="text-lg font-medium text-gray-900 mb-3">Recipients</h3>

        <!-- Send to All Non-Staff Toggle -->
        <SwitchGroup>
          <div class="flex items-center mb-4">
            <Switch
              v-model="sendAllTenants"
              :class="sendAllTenants ? 'bg-blue-600' : 'bg-gray-200'"
              class="relative inline-flex h-6 w-11 items-center rounded-full"
            >
              <span
                :class="sendAllTenants ? 'translate-x-6' : 'translate-x-1'"
                class="inline-block h-4 w-4 transform rounded-full bg-white transition"
              />
            </Switch>
            <SwitchLabel class="ml-2 select-none text-lg"
              >Send to all Non-Staff across selected buildings</SwitchLabel
            >
          </div>
        </SwitchGroup>

        <!-- Send to All Staff Toggle -->
        <SwitchGroup>
          <div class="flex items-center mb-4">
            <Switch
              v-model="sendAllStaff"
              :class="sendAllStaff ? 'bg-blue-600' : 'bg-gray-200'"
              class="relative inline-flex h-6 w-11 items-center rounded-full"
            >
              <span
                :class="sendAllStaff ? 'translate-x-6' : 'translate-x-1'"
                class="inline-block h-4 w-4 transform rounded-full bg-white transition"
              />
            </Switch>
            <SwitchLabel class="ml-2 select-none text-lg"
              >Send to all Staff across selected buildings</SwitchLabel
            >
          </div>
        </SwitchGroup>

        <!-- Note about simplified filtering -->
        <div class="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <i-heroicons-solid-information-circle class="h-5 w-5 text-blue-400" />
            </div>
            <div class="ml-3">
              <p class="text-sm text-blue-700">
                Multi-building alerts use simplified filtering. For detailed floor,
                tenant, and role filtering, use the single building alert option.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Recipients -->
      <div class="mb-6">
        <h3 class="text-lg font-medium text-gray-900 mb-3">Additional Recipients</h3>
        <div v-if="additionalRecipients.length > 0" class="flex flex-wrap gap-2 mb-3">
          <div
            v-for="(phoneNumber, index) in additionalRecipients"
            :key="phoneNumber"
            class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
          >
            {{ phoneNumber }}
            <button
              @click="removeRecipient(index)"
              class="ml-2 inline-flex items-center justify-center w-4 h-4 text-blue-400 hover:text-blue-600"
            >
              <i-heroicons-solid-x class="w-3 h-3" />
            </button>
          </div>
        </div>
        <div class="flex gap-2">
          <input
            v-model="newRecipient"
            type="tel"
            placeholder="Phone number"
            class="border rounded px-3 py-2 flex-1 max-w-xs"
            @keyup.enter="addRecipient"
          />
          <button
            @click="addRecipient"
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Add Number
          </button>
        </div>
      </div>

      <!-- Recipient Count and Actions -->
      <div class="mb-6">
        <div v-if="isCountLoading"><Loading /></div>
        <div v-else>
          <button
            class="text-sm font-medium py-2 px-4 mt-2 text-blue-900 bg-blue-200 border border-transparent rounded-md hover:bg-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
            @click="ViewAllRecipients"
          >
            View All Recipients ({{ filteredCount + additionalRecipients.length }})
          </button>
        </div>
      </div>

      <!-- Send Alert Button -->
      <div class="flex gap-4">
        <button
          @click="confirmSendAlert"
          :disabled="
            isSending ||
            filteredCount === 0 ||
            selectedPortals.length === 0 ||
            !message.trim()
          "
          class="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          <span v-if="isSending">Sending...</span>
          <span v-else
            >Send Alert to {{ filteredCount.toLocaleString() }} Recipients</span
          >
        </button>
      </div>

      <BasePopup
        :is-visible="isRecipientViewerVisible"
        title="All Recipients"
        :is-full-width="true"
        @close="isRecipientViewerVisible = false"
      >
        <div v-if="isAllRecipientsLoading"><Loading /></div>
        <div v-else>
          <table class="table-auto w-full mt-4 border-2 shadow text-sm">
            <thead class="font-sm font-bold">
              <tr>
                <td
                  class="cursor-pointer hover:bg-sky-300 hover:text-black"
                  @click="selectedSortType = SortType.Name"
                >
                  <span class="flex has-tooltip">
                    <span
                      class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                      >Click to sort by Recipient Name</span
                    >
                    Name
                    <i-heroicons-outline-chevron-down
                      class="ml-2 mt-1 text-gray-500"
                      v-if="selectedSortType !== SortType.Name"
                    />
                  </span>
                </td>
                <td
                  class="cursor-pointer hover:bg-sky-300 hover:text-black"
                  @click="selectedSortType = SortType.Role"
                >
                  <span class="flex has-tooltip">
                    <span
                      class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                      >Click to sort by Recipient Role</span
                    >
                    Role
                    <i-heroicons-outline-chevron-down
                      class="ml-2 mt-1 text-gray-500"
                      v-if="selectedSortType !== SortType.Role"
                    />
                  </span>
                </td>
                <td class="cursor-pointer" @click="selectedSortType = SortType.Buildings">
                  <span class="flex has-tooltip">
                    <span
                      class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                      >Click to sort by Recipient's Building(s)</span
                    >
                    Building
                    <i-heroicons-outline-chevron-down
                      class="ml-2 mt-1 text-gray-500"
                      v-if="selectedSortType !== SortType.Buildings"
                    />
                  </span>
                </td>
                <td class="cursor-pointer" @click="selectedSortType = SortType.Email">
                  <span class="flex has-tooltip">
                    <span
                      class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                      >Click to sort by Recipient's Email</span
                    >
                    Alert Email
                    <i-heroicons-outline-chevron-down
                      class="ml-2 mt-1 text-gray-500"
                      v-if="selectedSortType !== SortType.Email"
                    />
                  </span>
                </td>
                <td class="cursor-pointer" @click="selectedSortType = SortType.Phone">
                  <span class="flex has-tooltip">
                    <span
                      class="tooltip text-xs shadow-lg p-1 pl-3 bg-gray-200 text-gray-700 -mt-8"
                      >Click to sort by Recipient's Phone</span
                    >
                    Alert Phone
                    <i-heroicons-outline-chevron-down
                      class="ml-2 mt-1 text-gray-500"
                      v-if="selectedSortType !== SortType.Phone"
                    />
                  </span>
                </td>
              </tr>
            </thead>
            <tr
              v-for="(recipient, i) in sortedRecipients"
              :key="i"
              class="font-xs font-mono hover:border-sky-800 border-bottom-2 hover:bg-sky-300"
              :class="i % 2 ? 'bg-stone-200' : ''"
            >
              <td>{{ recipient.firstName }} {{ recipient.lastName }}</td>
              <td>{{ recipient.roleAlias }}</td>
              <td>
                {{
                  recipient.buildingNames &&
                  recipient.buildingNames.length > 0 &&
                  recipient.buildingNames.length < 50
                    ? recipient.buildingNames
                    : "Multiple Buildings"
                }}
              </td>
              <td>{{ recipient.contactEmail }}</td>
              <td :class="recipient.isSmsOptedOut ? 'text-red-600' : ''">
                {{ formatPhoneNumber(recipient.contactSms)
                }}{{
                  recipient.isSmsOptedOut ? " (Opted Out, will not receive text)" : ""
                }}
              </td>
            </tr>
          </table>
          <div v-if="additionalRecipients.length > 0">
            <h1 class="mt-5">Additional Recipients:</h1>
            <p
              v-for="additionalRecipient in additionalRecipients"
              :key="additionalRecipient"
            >
              {{ formatPhoneNumber(additionalRecipient) }}
            </p>
          </div>
        </div>
      </BasePopup>

      <!-- Confirmation Dialog -->
      <div
        v-if="showConfirmDialog"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div class="bg-white rounded-lg p-6 max-w-md w-full">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Confirm Alert Send</h3>
          <p class="text-sm text-gray-600 mb-6">
            Are you sure you want to send this alert to
            {{ filteredCount.toLocaleString() }} recipients across
            {{ selectedPortals.length }} buildings?
          </p>
          <div v-if="mobileMessage" class="text-sm text-gray-700 mb-6">
            <div class="font-medium">Email:</div>
            <div class="mb-2 italic">&quot;{{ message }}&quot;</div>
            <div class="font-medium">SMS / Push:</div>
            <div class="italic">&quot;{{ mobileMessage }}&quot;</div>
          </div>
          <div class="flex gap-4 justify-end">
            <button
              @click="showConfirmDialog = false"
              class="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              @click="sendAlert"
              :disabled="isSending"
              class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
            >
              <span v-if="isSending">Sending...</span>
              <span v-else>Send Alert</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Success/Error Messages -->
      <div v-if="alertMessage" class="fixed bottom-4 right-4 max-w-sm">
        <div
          :class="[
            'p-4 rounded-lg shadow-lg',
            alertMessage.type === 'success'
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-red-100 text-red-800 border border-red-200',
          ]"
        >
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <i-heroicons-solid-check-circle
                v-if="alertMessage.type === 'success'"
                class="h-5 w-5"
              />
              <i-heroicons-solid-x-circle v-else class="h-5 w-5" />
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium">{{ alertMessage.text }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useBldgStore } from "../../store/bldgStore";
import {
  TemplateDTO,
  FilteredAlertRecipientsRequest,
  FilteredAlertRecipientsCountRequest,
  AlertFilterDTO,
  SendAlertRequest,
  PortalDTO,
} from "../../store/generatedFetchClient";
import { useAlertStore } from "../../store/alertStore";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  SwitchGroup,
  Switch,
  SwitchLabel,
} from "@headlessui/vue";
import { formatPhoneNumber } from "../../helpers";

// Store setup
const bldgStore = useBldgStore();
const alertStore = useAlertStore();

// Core computed properties
const property = computed(() => bldgStore.building);
const portals = computed(() => bldgStore.portalList || []);

// Loading states
const isLoading = ref(false);
const isCountLoading = ref(false);
const isSending = ref(false);

// Portal/building selection
const selectedPortals = ref([] as PortalDTO[]);

// Template and message
const alertTemplates = computed(() => alertStore.templates);
const selectedTemplate = ref(null as TemplateDTO | null);
const message = ref("");

// AI short version for SMS / push (email always gets the full message)
const mobileSummaryPanel = ref<{
  generate: () => Promise<void>;
  reset: () => void;
  isLoading: boolean;
} | null>(null);
const mobileSummary = ref("");
const useMobileSummary = ref(false);
const mobileMessage = computed(() =>
  useMobileSummary.value && mobileSummary.value.trim() ? mobileSummary.value : null
);

// Simplified filtering
const sendAllTenants = ref(true);
const sendAllStaff = ref(true);

// Additional recipients
const additionalRecipients = ref([] as string[]);
const newRecipient = ref("");

// Recipients display
const recipients = ref([] as AlertFilterDTO[]);
const filteredCount = ref(0);
//const showViewRecipientsModal = ref(false);

// Modal and sorting variables to match AdminMultiAlert.vue
const isRecipientViewerVisible = ref(false);
const isAllRecipientsLoading = ref(false);

enum SortType {
  Name = 1,
  Role,
  Tenant,
  Floors,
  Buildings,
  Email,
  Phone,
}
const selectedSortType = ref(SortType.Name);

// UI state
const showConfirmDialog = ref(false);
const alertMessage = ref(null as { type: "success" | "error"; text: string } | null);

// Initialize data
onMounted(async () => {
  await loadData();
});

watch(property, loadData);

// Watch for changes that affect recipient count
watch(selectedPortals, getRecipientCount, { deep: true });
watch(sendAllTenants, getRecipientCount);
watch(sendAllStaff, getRecipientCount);

// Watch template selection
watch(selectedTemplate, () => {
  if (selectedTemplate.value) {
    message.value = selectedTemplate.value.template || "";
    mobileSummaryPanel.value?.reset();
    nextTick(() => mobileSummaryPanel.value?.generate());
  }
});

async function loadData() {
  isLoading.value = true;
  if (property?.value?.clientSystemName) {
    try {
      // Load portal list for multi-building selection
      await bldgStore.getPortalList(property.value.clientSystemName);

      // Pre-select current building
      const currentPortal = portals.value.find(
        (p) => p.portalId === property.value.portalId
      );
      if (currentPortal) {
        selectedPortals.value = [currentPortal];
      }

      // Load templates (using first selected portal or current portal)
      const portalForTemplates =
        selectedPortals.value[0]?.portalId || property.value.portalId;
      if (portalForTemplates) {
        await alertStore.getTemplates(portalForTemplates);
      }

      // Get initial recipient count
      await getRecipientCount();
    } catch (error) {
      console.error("Error loading data:", error);
    }
  }
  isLoading.value = false;
}

async function getRecipientCount() {
  if (selectedPortals.value.length === 0) {
    filteredCount.value = 0;
    recipients.value = [];
    return;
  }

  isCountLoading.value = true;

  try {
    const filter: FilteredAlertRecipientsCountRequest = {
      portalIds: selectedPortals.value.map((p) => p.portalId),
      floors: [], // Empty for multi-building
      staffRoles: [],
      nonStaffRoles: [],
      tenants: [],
      staffCollections: [],
      nonStaffCollections: [],
      allTenants: sendAllTenants.value,
      allStaff: sendAllStaff.value,
    };

    filteredCount.value = await alertStore.getFilteredCount(filter);
  } catch (error) {
    console.error("Error getting recipient count:", error);
    filteredCount.value = 0;
  }

  isCountLoading.value = false;
}

// Function to match AdminMultiAlert.vue naming
async function ViewAllRecipients() {
  isAllRecipientsLoading.value = true;
  isRecipientViewerVisible.value = true;
  await getRecipients();
  isAllRecipientsLoading.value = false;
}

async function getRecipients() {
  try {
    const filter: FilteredAlertRecipientsRequest = {
      portalIds: selectedPortals.value.map((p) => p.portalId),
      floors: [], // Empty for multi-building
      staffRoles: [],
      nonStaffRoles: [],
      tenants: [],
      staffCollections: [],
      nonStaffCollections: [],
      allTenants: sendAllTenants.value,
      allStaff: sendAllStaff.value,
    };

    recipients.value = await alertStore.getFilteredRecipients(filter);
  } catch (error) {
    console.error("Error getting recipients:", error);
    recipients.value = [];
  }
}

// Computed property for sorted recipients to match AdminMultiAlert.vue
const sortedRecipients = computed(() => {
  return recipients.value?.sort((a, b) => {
    if (selectedSortType.value == SortType.Name)
      return a.firstName?.localeCompare(b.firstName) || 0;
    else if (selectedSortType.value === SortType.Role)
      return a.roleAlias?.localeCompare(b.roleAlias) || 0;
    else if (selectedSortType.value === SortType.Buildings)
      return a.buildingNames?.localeCompare(b.buildingNames) || 0;
    else if (selectedSortType.value == SortType.Email)
      return a.contactEmail?.localeCompare(b.contactEmail) || 0;
    else if (selectedSortType.value === SortType.Phone)
      return a.contactSms?.localeCompare(b.contactSms) || 0;
    return 0;
  });
});

function selectAllBuildings() {
  selectedPortals.value = [...portals.value];
}

function clearBuildingSelection() {
  selectedPortals.value = [];
}

function addRecipient() {
  const phoneNumber = formatPhoneNumber(newRecipient.value.trim());
  if (phoneNumber && !additionalRecipients.value.includes(phoneNumber)) {
    additionalRecipients.value.push(phoneNumber);
    newRecipient.value = "";
  }
}

function removeRecipient(index: number) {
  additionalRecipients.value.splice(index, 1);
}

function confirmSendAlert() {
  if (!validateAlert()) return;
  showConfirmDialog.value = true;
}

function validateAlert(): boolean {
  if (selectedPortals.value.length === 0) {
    showAlert("error", "Please select at least one building");
    return false;
  }

  if (!message.value.trim()) {
    showAlert("error", "Please enter an alert message");
    return false;
  }

  if (filteredCount.value === 0) {
    showAlert("error", "No recipients found for selected buildings");
    return false;
  }

  if (mobileSummaryPanel.value?.isLoading) {
    showAlert("error", "Please wait for the mobile summary to finish generating");
    return false;
  }

  return true;
}

async function sendAlert() {
  isSending.value = true;
  showConfirmDialog.value = false;

  try {
    const filter: SendAlertRequest = {
      portalIds: selectedPortals.value.map((p) => p.portalId),
      portalName:
        selectedPortals.value.length === 1
          ? selectedPortals.value[0].name
          : `${selectedPortals.value.length} Buildings`,
      siteCode: property.value.siteCode,
      floors: [], // Empty for multi-building
      staffRoles: [],
      nonStaffRoles: [],
      tenants: [],
      staffCollections: [],
      nonStaffCollections: [],
      allTenants: sendAllTenants.value,
      allStaff: sendAllStaff.value,
      additionalPhoneNumbers: additionalRecipients.value,
      message: message.value,
      mobileMessage: mobileMessage.value,
      smsOriginationNumber: property.value.alertOriginationNumber,
    };

    const result = await alertStore.sendAlert(filter);

    if (result) {
      showAlert("error", result);
    } else {
      showAlert(
        "success",
        `Alert sent successfully to ${filteredCount.value.toLocaleString()} recipients!`
      );
      // Reset form
      message.value = "";
      selectedTemplate.value = null;
      additionalRecipients.value = [];
    }
  } catch (error) {
    console.error("Error sending alert:", error);
    showAlert("error", "Failed to send alert. Please try again.");
  }

  isSending.value = false;
}

function showAlert(type: "success" | "error", text: string) {
  alertMessage.value = { type, text };
  setTimeout(() => {
    alertMessage.value = null;
  }, 5000);
}
</script>
